import { useState } from "react";
import { ChevronDown, Filter } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { dictionary, type Language } from "@/utils/dictionary";
import { cn } from "@/lib/utils";
import { truncateProductName } from "../utils/truncateProductName";

export interface ProductFilterItem {
  product_id: string;
  name: string;
  image_url?: string;
  creativesCount: number;
}

interface ProductFilterDropdownProps {
  uniqueProducts: ProductFilterItem[];
  selectedProductId: string | null;
  onSelect: (productId: string | null) => void;
  language: Language;
  alignRight?: boolean;
}

export function ProductFilterDropdown({
  uniqueProducts,
  selectedProductId,
  onSelect,
  language,
  alignRight = false,
}: ProductFilterDropdownProps) {
  const [showDropdown, setShowDropdown] = useState(false);

  const totalCount = uniqueProducts.reduce((sum, p) => sum + p.creativesCount, 0);
  const selectedProduct = uniqueProducts.find((p) => p.product_id === selectedProductId);

  const handleSelect = (productId: string | null) => {
    onSelect(productId);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className={cn(
          "flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 border",
          selectedProductId
            ? "bg-fluxi text-white border-fluxi shadow-sm"
            : "bg-white text-gray-500 border-gray-200 shadow-sm hover:border-gray-300 hover:text-gray-700"
        )}
      >
        <Filter className="h-4 w-4" />
      </button>

      <AnimatePresence>
        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-20"
              aria-hidden
              onClick={() => setShowDropdown(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 4, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.96 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className={cn(
                "absolute top-full mt-2 z-30 w-64 bg-background border border-border/40 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto",
                alignRight ? "right-0" : "left-0"
              )}
            >
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors",
                  selectedProductId === null
                    ? "bg-primary/5 text-primary font-medium"
                    : "text-foreground hover:bg-muted/50 text-gray-900 hover:bg-gray-50"
                )}
              >
                <span>
                  {dictionary["all-products"]?.[language] || "Todos los productos"}
                </span>
                <span className="text-xs text-muted-foreground text-gray-500 ml-auto">
                  {totalCount}
                </span>
              </button>

              <div className="border-t border-border/30 border-gray-200/30" />

              {uniqueProducts.map((product) => (
                <button
                  key={product.product_id}
                  type="button"
                  onClick={() => handleSelect(product.product_id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors",
                    selectedProductId === product.product_id
                      ? "bg-primary/5 text-primary font-medium"
                      : "text-foreground hover:bg-muted/50 text-gray-900 hover:bg-gray-50"
                  )}
                >
                  {product.image_url ? (
                    <div className="h-7 w-7 rounded-lg bg-card border border-border/30 overflow-hidden flex-shrink-0 bg-white border-gray-200/30">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="h-full w-full object-contain p-0.5"
                      />
                    </div>
                  ) : (
                    <div className="h-7 w-7 rounded-lg bg-gray-100 border border-gray-200/30 flex-shrink-0" />
                  )}
                  <span className="truncate flex-1">{truncateProductName(product.name)}</span>
                  <span className="text-xs text-muted-foreground text-gray-500">
                    {product.creativesCount}
                  </span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
